import forge from 'apis/forge';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { selectOrganizations, selectScenes } from 'redux/db/reducers';
import { setDefaultOrganizationId, setPendingDsPlan } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import CreatorInput from '../CreatorInput';
import * as Base from '../styles';
import SceneLinkToggle from './SceneLinkToggle';

const DesignSystemCreator = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const sceneId = searchParams.get('sceneId');
	const urlOrganizationId = searchParams.get('organizationId');
	const [loadingMessage, setLoadingMessage] = useState<string>('');
	const [linkToScene, setLinkToScene] = useState<boolean>(true);

	const { scenes, organizations, defaultOrganizationId } = useSelector(
		(r: RootReducer) => ({
			scenes: selectScenes(r.db),
			organizations: selectOrganizations(r.db),
			defaultOrganizationId: r.mode.defaultOrganizationId,
		}),
		shallowEqual
	);
	const scene = sceneId ? scenes.find((s) => s.id === sceneId) : null;
	const selectedOrganizationId = urlOrganizationId || defaultOrganizationId;

	// Validate that saved org still exists (only clear Redux default, not URL param)
	useEffect(() => {
		if (defaultOrganizationId && organizations.length > 0) {
			const orgExists = organizations.some((org) => org.id === defaultOrganizationId);
			if (!orgExists) {
				dispatch(setDefaultOrganizationId(null));
			}
		}
	}, [organizations, defaultOrganizationId, dispatch]);

	// Default linkToScene to true if sceneId is present
	useEffect(() => {
		if (sceneId) {
			setLinkToScene(true);
		}
	}, [sceneId]);

	const onOwnershipSelect = (organizationId: string | null) => {
		dispatch(setDefaultOrganizationId(organizationId));
	};

	const onGeneratePlan = async (prompt: string, attachments: Attachment[]) => {
		try {
			setLoadingMessage('analyzing requirements...');
			const res = await forge.planDesignSystem.withToast()({
				prompt,
				attachments,
			});
			dispatch(setPendingDsPlan(res.plan));
			setLoadingMessage('');
			navigate(
				routes.createDesignSystemPlan(
					linkToScene && sceneId ? sceneId : undefined,
					selectedOrganizationId || undefined
				)
			);
		} catch (e) {
			setLoadingMessage('');
		}
	};

	return (
		<Base.Base>
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<Base.Content>
				<Base.Input>
					<Base.Title>Create a design system</Base.Title>
					<CreatorInput
						onSubmit={onGeneratePlan}
						disabled={!!loadingMessage}
						mode="designSystemPlan"
						organizationId={selectedOrganizationId}
						organizations={organizations}
						onOwnershipSelect={onOwnershipSelect}
					/>
					{sceneId && scene && (
						<SceneLinkToggle
							sceneName={scene.title}
							checked={linkToScene}
							onChange={setLinkToScene}
						/>
					)}
				</Base.Input>
			</Base.Content>
		</Base.Base>
	);
};

export default DesignSystemCreator;
